import React, { useState, useEffect } from "react";
import { Dropdown } from "react-bootstrap";
import { BsGrid3X3GapFill, BsFillEnvelopeFill } from "react-icons/bs";
import BellIconWithNotifications, {
  NotificationWidget,
} from "../notifications/notificationwidget";
import { IoMap } from "react-icons/io5";
import { FiRefreshCw } from "react-icons/fi";
import { FaUserPlus } from "react-icons/fa";
import "bootstrap/dist/css/bootstrap.min.css";
import { useNavigate } from "react-router-dom";

const ManageRoles = () => {
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;

  // State management
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [userName, setUserName] = useState("");

  // Fetch roles from API
  const fetchRoles = async () => {
    try {
      setLoading(true);
      setError("");
      const token = localStorage.getItem("accessToken");

      if (!token) {
        setError("Authentication token not found");
        return;
      }

      const response = await fetch(`${apiUrl}renewals/roles`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        setRoles(data.data || []);
      } else {
        const errorData = await response.json();
        setError(errorData.message || "Failed to fetch roles");
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
      setError("Failed to fetch roles");
    } finally {
      setLoading(false);
    }
  };

  // Delete role function
  const deleteRole = async (roleId) => {
    if (!window.confirm("Are you sure you want to delete this role?")) {
      return;
    }

    try {
      const token = localStorage.getItem("accessToken");

      const response = await fetch(`${apiUrl}renewals/roles/${roleId}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          isActive: false,
          lastUpdatedBy: userName || "system",
        }),
      });

      if (response.ok) {
        // Refresh roles list
        fetchRoles();
      } else {
        const errorData = await response.json();
        setError(errorData.message || "Failed to delete role");
      }
    } catch (error) {
      console.error("Error deleting role:", error);
      setError("Failed to delete role");
    }
  };

  // Load roles on component mount and get user info
  useEffect(() => {
    // Get user info from localStorage
    const user = JSON.parse(localStorage.getItem("user"));
    if (user && user.name) {
      setUserName(user.name);
    }

    // Fetch roles
    fetchRoles();
  }, []);

  // Event handlers
  const handleAddUser = (roleId) => {
    console.log(`Add user to role ${roleId}`);
    navigate("/renewals-add-users-to-role", { state: { roleId } });
  };

  const handleEdit = (roleId) => {
    console.log(`Edit role ${roleId}`);
    navigate("/renewals-edit-role", { state: { roleId } });
  };

  const handleDelete = (roleId) => {
    deleteRole(roleId);
  };

  const handleAddNewRole = () => {
    navigate("/renewals-add-roles");
  };

  const handleRefresh = () => {
    fetchRoles();
  };

  // Format timestamp helper
  const formatTimestamp = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

  return (
    <>
      <div className="dashboard-title mb-3">
        <h1>Manage Roles</h1>
        <div className="d-flex align-items-center gap-3">
          <button
            className="btn btn-secondary d-none d-md-block"
            onClick={handleRefresh}
            disabled={loading}
          >
            {loading ? "Loading..." : "Refresh"}
          </button>
          <button
            className="btn btn-primary d-none d-md-block"
            onClick={handleAddNewRole}
          >
            Add New Role
          </button>
          <button
            className="btn btn-primary btn-sm d-block d-md-none"
            onClick={handleAddNewRole}
          >
            <FaUserPlus />
          </button>
          <BellIconWithNotifications />
          <div className="dropdown-container">
            <Dropdown>
              <Dropdown.Toggle
                variant="light"
                id="dropdown-basic"
                className="rounded-circle border-0 bg-transparent"
              >
                <div
                  className="menu-icon"
                  style={{
                    fontSize: "1.5rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <BsGrid3X3GapFill />
                </div>
              </Dropdown.Toggle>

              <Dropdown.Menu className="dropdown-menu-custom">
                <Dropdown.Item
                  href="/dashboard"
                  className="dropdown-item-custom"
                >
                  <BsFillEnvelopeFill className="dropdown-icon" /> Quote/Bind
                  Email
                </Dropdown.Item>
                <Dropdown.Item
                  href="/geographical-concentration"
                  className="dropdown-item-custom"
                >
                  <IoMap className="dropdown-icon" /> Geographical Concentration
                </Dropdown.Item>
                <Dropdown.Item
                  href="/renewals-dashboard"
                  className="dropdown-item-custom"
                >
                  <FiRefreshCw className="dropdown-icon" /> Renewals
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      </div>

      <div className="content-body">
        {/* Error Display */}
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
            <button
              type="button"
              className="btn-close float-end"
              onClick={() => setError("")}
              aria-label="Close"
            ></button>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 text-muted">Loading roles...</p>
          </div>
        )}

        {/* Roles Table */}
        {!loading && (
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead style={{ backgroundColor: "#f8f9fa" }}>
                <tr>
                  <th>#</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Last Updated</th>
                  <th>Created</th>
                  <th className="text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {roles.length === 0 && !loading ? (
                  <tr>
                    <td colSpan="6" className="text-center py-4 text-muted">
                      No roles found. Click "Add New Role" to create one.
                    </td>
                  </tr>
                ) : (
                  roles.map((role, index) => (
                    <tr key={role.id}>
                      <td>{index + 1}</td>
                      <td className="fw-medium">{role.role}</td>
                      <td>
                        <span
                          className={`badge ${
                            role.isActive ? "bg-success" : "bg-secondary"
                          }`}
                        >
                          {role.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="text-muted">
                        {formatTimestamp(role.updatedAt)}
                        {role.lastUpdatedBy && (
                          <small className="d-block text-muted">
                            by {role.lastUpdatedBy}
                          </small>
                        )}
                      </td>
                      <td className="text-muted">
                        {formatTimestamp(role.createdAt)}
                      </td>
                      <td>
                        <div className="d-flex justify-content-end gap-2">
                          <button
                            className="btn btn-sm text-white"
                            onClick={() => handleAddUser(role.id)}
                            style={{
                              backgroundColor: "#122066",
                              border: "none",
                            }}
                            disabled={!role.isActive}
                          >
                            Add User
                          </button>
                          <button
                            className="btn btn-sm text-white"
                            onClick={() => handleEdit(role.id)}
                            style={{
                              backgroundColor: "#122066",
                              border: "none",
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => handleDelete(role.id)}
                            disabled={!role.isActive}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Summary Stats */}
        {!loading && roles.length > 0 && (
          <div className="mt-3">
            <small className="text-muted">
              Total roles: {roles.length} | Active:{" "}
              {roles.filter((r) => r.isActive).length} | Inactive:{" "}
              {roles.filter((r) => !r.isActive).length}
            </small>
          </div>
        )}
      </div>
    </>
  );
};

export default ManageRoles;
